import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, getActivities, normPhone } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang, actLabel } from "@/lib/i18n";

const DOCS = [["aadhaar", "Aadhaar (last 4 digits only)"], ["pan", "PAN"], ["passport", "Passport"], ["driving_licence", "Driving Licence"], ["voter_id", "Voter ID"]];
const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

const Field = ({ label, children }) => <label className="block text-sm font-semibold text-slate-700 space-y-1.5"><span>{label}</span>{children}</label>;

const Consent = ({ id, checked, onChange, children }) => (
  <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
    <Checkbox data-testid={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" /><span>{children}</span>
  </label>
);

export default function BecomeCompanion() {
  const { lang } = useLang();
  const { signIn, user } = useAuth();
  const nav = useNavigate();
  const [acts, setActs] = useState([]);
  const [f, setF] = useState({ name: "", email: "", password: "", dob: "", phone: "", gender: "", city: "Dumka", display_name: "", languages: "Hindi, English", interests: "", activities: [], hourly_rate: 500, bio: "", id_doc_type: "", id_last4: "", consent_age: false, consent_rules: false, consent_privacy: false });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleAct = (s) => setF({ ...f, activities: f.activities.includes(s) ? f.activities.filter((x) => x !== s) : [...f.activities, s] });

  useEffect(() => {
    getActivities().then(setActs).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const body = { ...f, languages: split(f.languages), interests: split(f.interests), hourly_rate: Number(f.hourly_rate), accept_terms: f.consent_rules && f.consent_privacy };
      const { data } = await api.post("/auth/register-companion", body);
      signIn(data.token, data.user);
      nav("/companion");
    } catch (e2) {
      setErr(errMsg(e2));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setBusy(false);
    }
  };

  if (user) return <div className="container-x py-24 text-center text-slate-600" data-testid="become-logged-in">You're logged in. Log out first to apply with a new companion account.</div>;

  return (
    <div className="container-x py-12 max-w-3xl" data-testid="become-companion-page">
      <p className="eyebrow">Companion application</p>
      <h1 className="text-4xl sm:text-5xl font-bold mt-2">Become a RentMyDate companion</h1>
      <p className="text-slate-600 mt-3">Adults 18+ only. Your profile is published only after age and identity verification by our team. We collect only what is necessary.</p>
      {err && <p className="mt-6 rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-700" data-testid="companion-register-error">{err}</p>}
      <form onSubmit={submit} className="mt-8 space-y-10" noValidate>
        <section className="card-surface p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
          <h2 className="sm:col-span-2 text-2xl font-semibold">1. Account (private)</h2>
          <Field label="Legal full name"><Input data-testid="cr-name-input" value={f.name} onChange={up("name")} /></Field>
          <Field label="Email"><Input data-testid="cr-email-input" type="email" value={f.email} onChange={up("email")} /></Field>
          <Field label="Password (min 8)"><PasswordInput data-testid="cr-password-input" value={f.password} onChange={up("password")} /></Field>
          <div className="text-sm font-semibold text-slate-700 space-y-1.5"><span>Date of birth (18+)</span><DatePicker testId="cr-dob-input" dob value={f.dob} onChange={(v) => setF({ ...f, dob: v })} /></div>
          <Field label="Mobile (only RentMyDate staff can see it)"><Input data-testid="cr-phone-input" inputMode="numeric" placeholder="10-digit Indian mobile" value={f.phone} onChange={(e) => setF({ ...f, phone: normPhone(e.target.value) })} /></Field>
          <Field label="Gender">
            <Select value={f.gender} onValueChange={(v) => setF({ ...f, gender: v })}>
              <SelectTrigger data-testid="cr-gender-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem></SelectContent>
            </Select>
          </Field>
        </section>
        <section className="card-surface p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
          <h2 className="sm:col-span-2 text-2xl font-semibold">2. Public profile</h2>
          <Field label="Display name (e.g. first name + initial)"><Input data-testid="cr-display-name-input" value={f.display_name} onChange={up("display_name")} /></Field>
          <Field label="City"><Input data-testid="cr-city-input" value={f.city} onChange={up("city")} /></Field>
          <Field label="Languages (comma separated)"><Input data-testid="cr-languages-input" value={f.languages} onChange={up("languages")} /></Field>
          <Field label="Interests (comma separated)"><Input data-testid="cr-interests-input" value={f.interests} onChange={up("interests")} /></Field>
          <Field label="Hourly rate (₹)"><Input data-testid="cr-rate-input" type="number" min={200} max={5000} value={f.hourly_rate} onChange={up("hourly_rate")} /></Field>
          <div className="sm:col-span-2 space-y-2">
            <p className="text-sm font-semibold text-slate-700">Activities you offer</p>
            <div className="flex flex-wrap gap-2">
              {acts.map((a) => (
                <button type="button" key={a.slug} onClick={() => toggleAct(a.slug)} data-testid={`cr-activity-${a.slug}`}
                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${f.activities.includes(a.slug) ? "bg-[#0D5C46] text-white border-[#0D5C46]" : "bg-white hover:border-[#0D5C46]"}`}>{actLabel(a, lang)}</button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2"><Field label="Short bio (20–600 characters)"><Textarea data-testid="cr-bio-input" rows={4} value={f.bio} onChange={up("bio")} maxLength={600} /></Field></div>
          <p className="sm:col-span-2 text-xs text-slate-500">Profile photo upload is disabled in this demo. An initial-letter avatar is used until photo upload with moderation is enabled.</p>
        </section>
        <section className="card-surface p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
          <h2 className="sm:col-span-2 text-2xl font-semibold flex items-center gap-2"><Lock size={20} />3. Identity verification</h2>
          <p className="sm:col-span-2 text-sm text-slate-600 rounded-lg bg-amber-50 border border-amber-200 p-3">Privacy: identity documents are never uploaded to public storage or shown on your profile. We record only the document type and last 4 characters; RentMyDate staff verify your age (18+) and identity before approval. A licensed KYC/DigiLocker integration is pending owner approval.</p>
          <Field label="Document type">
            <Select value={f.id_doc_type} onValueChange={(v) => setF({ ...f, id_doc_type: v })}>
              <SelectTrigger data-testid="cr-doc-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{DOCS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Last 4 characters"><Input data-testid="cr-doc-last4-input" maxLength={4} value={f.id_last4} onChange={up("id_last4")} /></Field>
        </section>
        <section className="space-y-3">
          <Consent id="cr-consent-age" checked={f.consent_age} onChange={(v) => setF({ ...f, consent_age: v })}>I confirm I am 18 years or older and the information provided is true.</Consent>
          <Consent id="cr-consent-rules" checked={f.consent_rules} onChange={(v) => setF({ ...f, consent_rules: v })}>I agree to the Terms of Service and Community Guidelines. I will provide only platonic, lawful social companionship and never offer sexual services.</Consent>
          <Consent id="cr-consent-privacy" checked={f.consent_privacy} onChange={(v) => setF({ ...f, consent_privacy: v })}>I consent to processing of my personal data as per the Privacy Policy (DPDP Act, 2023).</Consent>
        </section>
        <Button data-testid="cr-submit-btn" className="w-full h-12 rounded-full text-base" disabled={busy}>{busy ? <Loader2 className="animate-spin" /> : <><ShieldCheck size={18} className="mr-2" />Submit application</>}</Button>
      </form>
    </div>
  );
}
